import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { FaCopy } from 'react-icons/fa';

import Button from 'common/components/button/Button';
import Modal from 'common/components/modal/Modal';
import Select from 'common/components/select/Select';
import Label from 'common/components/label/Label';
import InputResizable from 'common/components/input/InputResizable';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { Data } from 'common/components/spreadsheet/types';
import { matchOptionValue } from 'common/components/spreadsheet/cellValues';

type Props = {
    vehicles: SelectValueLabel[];
    rows: Data<string>[];
    selectedRowIds: string[];
    setRows: (rows: Data<string>[]) => void;
};

// Task #66347: separadores com que uma lista de matrículas pode vir colada — uma por linha (a coluna
// do Excel), ou seguidas com vírgulas, ponto e vírgula ou tabulações.
const PLATE_SEPARATORS = /[\n\r\t;,|]+/;

type PasteOutcome = { added: number; alreadyThere: number; notFound: string[] };

// Task #66347: um contrato de frota é o mesmo contrato para vários ativos. Como na base de dados um
// contrato pertence sempre a um ativo, replicamos a linha: escolhe-se a linha modelo, escolhem-se os
// ativos, e a grelha passa a ter uma linha por ativo — cada uma continua editável à parte.
// Escolher 30 ativos a um clique cada não é trabalho para ninguém e a lista chega quase sempre já
// escrita algures — daí a caixa de colar, que a transforma na seleção de uma vez.
const ReplicateRowsButton = ({ vehicles, rows, selectedRowIds, setRows }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedVehicles, setSelectedVehicles] = useState<SelectValueLabel[]>([]);
    const [plateText, setPlateText] = useState('');
    const [outcome, setOutcome] = useState<PasteOutcome | null>(null);

    const sourceRow = useMemo(
        () => (selectedRowIds.length === 1 ? rows.find(row => (row as any).__index === selectedRowIds[0]) : undefined),
        [rows, selectedRowIds]);

    const canReplicate = !!sourceRow;

    const vehicleOptions = useMemo(
        () => vehicles.map(vehicle => ({ label: vehicle.label ?? '', value: vehicle.value ?? '' })),
        [vehicles]);

    const open = () => {
        if (!canReplicate) {
            addToast(t('bulk_contracts.replicate.select_one_row'), { appearance: 'warning' });
            return;
        }
        setSelectedVehicles([]);
        setPlateText('');
        setOutcome(null);
        setIsOpen(true);
    };

    // Cada pedaço de texto é casado com a matrícula do ativo pela mesma regra que a grelha usa ao
    // colar (ignora pontuação, acentos e maiúsculas), para "06ZI06" encontrar o "06-ZI-06".
    const addPlates = useCallback((text: string) => {
        const tokens = text.split(PLATE_SEPARATORS).map(part => part.trim()).filter(Boolean);
        if (!tokens.length) {
            setOutcome({ added: 0, alreadyThere: 0, notFound: [] });
            return;
        }

        const chosen = new Set(selectedVehicles.map(vehicle => vehicle.value));
        const found: SelectValueLabel[] = [];
        const notFound: string[] = [];
        let alreadyThere = 0;

        tokens.forEach(token => {
            const value = matchOptionValue(vehicleOptions, token);
            const vehicle = vehicles.find(option => option.value === value);
            if (!vehicle) {
                notFound.push(token);
                return;
            }
            // A mesma matrícula duas vezes na lista colada não pode dar dois contratos iguais.
            if (chosen.has(vehicle.value)) {
                alreadyThere += 1;
                return;
            }
            chosen.add(vehicle.value);
            found.push(vehicle);
        });

        if (found.length) setSelectedVehicles([...selectedVehicles, ...found]);
        setOutcome({ added: found.length, alreadyThere, notFound });
    }, [selectedVehicles, vehicleOptions, vehicles]);

    const confirm = () => {
        if (!sourceRow || !selectedVehicles.length) return;

        // Tudo o que a linha modelo tem menos o ativo — é isso que se replica.
        const { __index, __errors, vehicleId: sourceVehicleId, ...values } = sourceRow as any;

        const next = [...rows];
        const added: Data<string>[] = [];
        let updated = 0;

        selectedVehicles.forEach(vehicle => {
            // O ativo da própria linha modelo já tem a informação toda — não gera linha nenhuma.
            if (vehicle.value === sourceVehicleId) {
                updated += 1;
                return;
            }

            // Estando o ativo já na grelha, replica-se a informação para essa linha em vez de
            // acrescentar uma segunda linha do mesmo ativo (que era um contrato duplicado). A linha
            // fica onde está e mantém o seu __index, para não perder seleção nem avisos.
            const existingIndex = next.findIndex(row => row !== sourceRow && row.vehicleId === vehicle.value);
            if (existingIndex >= 0) {
                next[existingIndex] = { ...next[existingIndex], ...values, vehicleId: vehicle.value };
                updated += 1;
                return;
            }

            added.push({ ...values, vehicleId: vehicle.value } as Data<string>);
        });

        // A linha modelo NÃO se perde: continua com o seu ativo e as novas entram logo a seguir, para
        // o utilizador ver o bloco todo junto.
        const sourceIndex = next.findIndex(row => row === sourceRow);
        next.splice(sourceIndex + 1, 0, ...added);
        setRows(next);

        setIsOpen(false);
        addToast(updated
            ? t('bulk_contracts.replicate.done_mixed', { added: added.length, updated })
            : t('bulk_contracts.replicate.done', { count: added.length }),
        { appearance: 'success' });
    };

    return (
        <>
            <Button preset={'primary'} size={'small'} onlyIcon={true}
                onClick={open}
                disabledMessage={t('bulk_contracts.replicate.button_title') as string}
            >
                <FaCopy />
            </Button>

            <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}
                style={{ content: { width: '52rem', maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto', padding: '2rem' } }}>
                <h3 style={{ margin: 0 }}>{t('bulk_contracts.replicate.modal_title')}</h3>
                <p style={{ margin: '0.5rem 0 0', color: 'rgba(0, 0, 0, 0.6)' }}>
                    {t('bulk_contracts.replicate.modal_hint')}
                </p>

                <div style={{ marginTop: '1.5rem' }}>
                    <Label>{t('bulk_contracts.replicate.validate_label')}</Label>
                    <InputResizable
                        rows={4}
                        value={plateText}
                        placeholder={t('bulk_contracts.replicate.validate_placeholder') as string}
                        onChange={event => setPlateText(event.currentTarget.value)}
                        onPaste={event => {
                            const text = event.clipboardData?.getData('text/plain');
                            if (!text) return;
                            // A lista colada é validada na hora — o texto não fica na caixa para não
                            // dar a entender que ainda falta carregar em alguma coisa.
                            event.preventDefault();
                            setPlateText('');
                            addPlates(text);
                        }}
                    />

                    {/* O resultado à esquerda e o botão à direita: a coluna da direita é onde estão as
                        ações em toda a janela (o "Replicar" ali em baixo), e o resultado pode ocupar
                        duas ou três linhas sem empurrar nada. */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1.5rem', marginTop: '0.75rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: 0, fontSize: '0.9rem', wordBreak: 'break-word' }}>
                            {outcome && (outcome.added > 0
                                ? <span style={{ color: '#1BB99A' }}>{t('bulk_contracts.replicate.validate_added', { count: outcome.added })}</span>
                                : <span style={{ color: '#E6A100' }}>{t('bulk_contracts.replicate.validate_none')}</span>)}

                            {!!outcome?.alreadyThere &&
                                <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                                    {t('bulk_contracts.replicate.validate_already_there', { count: outcome.alreadyThere })}
                                </span>}

                            {!!outcome?.notFound.length &&
                                <span style={{ color: '#e04f5f' }}>
                                    {t('bulk_contracts.replicate.validate_not_found', {
                                        count: outcome.notFound.length,
                                        plates: outcome.notFound.join(', '),
                                    })}
                                </span>}
                        </div>

                        <Button preset={'primary'} size={'small'} bw={false}
                            isDisabled={!plateText.trim()}
                            onClick={() => { addPlates(plateText); setPlateText(''); }}
                            text={t('bulk_contracts.replicate.validate_button') as string} />
                    </div>
                </div>

                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #EAEAEA' }}>
                    {/* O contador e o "Limpar" ficam acima da lista, com folga: encostados à caixa liam-se
                        como se fizessem parte dela. */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
                        {/* Sem a margem de baixo do Label, que numa linha centrada o puxava para cima. */}
                        <Label style={{ margin: 0 }}>{t('bulk_contracts.columns.vehicle')}</Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.9rem' }}>
                                {t('bulk_contracts.replicate.selected_count', { count: selectedVehicles.length })}
                            </span>
                            {!!selectedVehicles.length &&
                                <Button preset={'secondary'} size={'small'} bw={false}
                                    onClick={() => { setSelectedVehicles([]); setOutcome(null); }}
                                    text={t('common.clear') as string} />}
                        </div>
                    </div>
                    {/* Com 30 ativos escolhidos as etiquetas não cabem no modal — a lista tem o seu próprio scroll. */}
                    <div style={{ maxHeight: '12rem', overflowY: 'auto' }}>
                        <Select
                            isMulti
                            usePortal
                            options={vehicleOptions}
                            value={selectedVehicles}
                            placeholder={t('bulk_contracts.replicate.vehicles_placeholder')}
                            onChange={(values: any) => setSelectedVehicles((values ?? []) as SelectValueLabel[])}
                        />
                    </div>
                </div>

                <div style={{ textAlign: 'right', marginTop: '2rem' }}>
                    <Button preset={'secondary'} onClick={() => setIsOpen(false)} text={t('common.cancel') as string} />
                    <Button onClick={confirm} isDisabled={!selectedVehicles.length}
                        text={t('bulk_contracts.replicate.confirm', { count: selectedVehicles.length }) as string} />
                </div>
            </Modal>
        </>
    );
};

export default ReplicateRowsButton;
